import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  CircularProgress
} from '@mui/material';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import moment from 'moment';
import { useSurveyFieldDetails } from '../components/api/queries/hooks';

const getStatusColor = (status) => {
  const upperStatus = (status || '').toUpperCase();
  if (upperStatus === 'SUBMITTED') return { bg: '#e6f4ea', text: '#1e8e3e' };
  if (upperStatus === 'REJECTED') return { bg: '#fce8e6', text: '#d93025' };
  return { bg: '#fff3e0', text: '#e65100' }; // DRAFT or default
};

const ReadOnlyDependentFields = ({ options, answerData }) => {
  const choices = options?.choices || [];
  if (!choices || !answerData || !answerData.dependent_fields) return null;

  const selectedValues = Array.isArray(answerData.value) ? answerData.value : [answerData.value];

  return (
    <>
      {selectedValues.map((selectedValue) => {
        // Find the choice object for this value
        const selectedChoice = choices.find((c) => c.value === selectedValue);
        if (!selectedChoice) return null;
        const dependentFields = selectedChoice.dependent_fields || options?.dependent_fields || [];
        const dependentData = answerData?.dependent_fields[selectedValue] || {};

        return (
          <Box key={selectedValue} sx={{ mt: 1, pl: 3 }}>
            {dependentFields?.map((df, idx) => {
              const val = dependentData[df.key];
              if (val === undefined || val === null || val === '') return null;

              const displayVal = `${val} ${
                df.type === 'NUMBER' && df.label.toLowerCase().includes('cm') ? 'cm' : ''
              }${
                df.type === 'NUMBER' && df.label.toLowerCase().includes('l/acre') ? 'L/acre' : ''
              }`.trim();

              return (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-end', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                    ↳ {df.label}
                  </Typography>
                  <Box
                    sx={{
                      flexGrow: 1,
                      borderBottom: '2px dotted #e0e0e0',
                      mx: 2,
                      position: 'relative',
                      top: '-5px'
                    }}
                  />
                  <Typography variant="body2" fontWeight="medium" sx={{ flexShrink: 0 }}>
                    {displayVal}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        );
      })}
    </>
  );
};

const ReadOnlyQuestionRow = ({ question }) => {
  const { answer } = question;

  const hasAnswer =
    answer &&
    answer?.value !== null &&
    answer?.value !== undefined &&
    answer?.value !== '' &&
    !(Array.isArray(answer?.value) && answer?.value.length === 0);

  // Hide empty array for checkbox
  if (Array.isArray(answer?.value) && answer?.value.length === 0) return null;

  // Render logic based on array vs primitive
  let renderedAnswer = null;

  if (!hasAnswer) {
    renderedAnswer = (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ fontStyle: 'italic', textAlign: 'right' }}
      >
        Not answered
      </Typography>
    );
  } else if (Array.isArray(answer.value)) {
    renderedAnswer = (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'flex-end' }}>
        {answer?.value?.map((val) => {
          const choice = question.options?.choices?.find((c) => c.value === val);
          return (
            <Chip
              key={val}
              label={choice ? choice.label : val}
              size="small"
              sx={{ bgcolor: '#f0f0f0', fontWeight: 'medium', height: 20, fontSize: '0.75rem' }}
            />
          );
        })}
      </Box>
    );
  } else {
    const choice = question?.options?.choices?.find((c) => c.value === answer.value);
    const displayVal = choice ? choice.label : answer.value;
    renderedAnswer = (
      <Typography variant="body1" fontWeight="medium" sx={{ textAlign: 'right' }}>
        {displayVal}
      </Typography>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', mb: 0.5 }}>
        <Typography variant="body1" color="text.primary" sx={{ flexShrink: 0, maxWidth: '60%' }}>
          {question.question}
        </Typography>
        <Box
          sx={{
            flexGrow: 1,
            borderBottom: '2px dotted #e0e0e0',
            mx: 2,
            position: 'relative',
            top: '-6px'
          }}
        />
        <Box sx={{ flexShrink: 0, maxWidth: '35%' }}>{renderedAnswer}</Box>
      </Box>
      <ReadOnlyDependentFields options={question.options} answerData={answer} />
    </Box>
  );
};

export const SurveyFieldDetailsPage = ({
  fieldId: propFieldId,
  surveyId: propSurveyId,
  farmerName,
  onClose
}) => {
  const fieldId = propFieldId;
  const surveyId = propSurveyId;

  const [activeYear, setActiveYear] = useState(null);

  const { data: surveyFieldData, isLoading } = useSurveyFieldDetails(fieldId, surveyId);

  useEffect(() => {
    setActiveYear(null);
  }, [fieldId, surveyId]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!surveyFieldData) {
    return (
      <Box p={4}>
        <Typography color="error">Failed to load survey data or missing parameters.</Typography>
      </Box>
    );
  }

  const statusColor = getStatusColor(surveyFieldData.status);

  if (!activeYear) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f9f9f9', pb: 4 }}>
        <Box
          sx={{
            bgcolor: 'white',
            px: 2,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #eaeaea'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton sx={{ mr: 1 }} onClick={onClose || (() => window.close())}>
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
                {surveyFieldData.name}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ p: 2, maxWidth: 600, mx: 'auto' }}>
          <Box
            sx={{
              border: '1px solid #eaeaea',
              borderRadius: 2,
              p: 2,
              mb: 3,
              bgcolor: 'white',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5
            }}
          >
            <Typography variant="subtitle1" sx={{ lineHeight: 1.2 }}>
              <span style={{ fontWeight: 600 }}>Farmer-</span>{' '}
              <span style={{ fontWeight: 400 }}>{farmerName || ''}</span>
            </Typography>
            {surveyFieldData.createdAt && (
              <Typography component="div" variant="body2" sx={{ lineHeight: 1.2 }}>
                <span style={{ fontWeight: 700 }}>Created At-</span>{' '}
                <span style={{ fontWeight: 400 }}>
                  {moment(surveyFieldData.createdAt).format('MMMM Do YYYY, h:mm:ss a')}
                </span>
              </Typography>
            )}

            {surveyFieldData.updatedAt && (
              <Typography component="div" variant="body2" sx={{ lineHeight: 1.2 }}>
                <span style={{ fontWeight: 700 }}>Last Updated At-</span>{' '}
                <span style={{ fontWeight: 400 }}>
                  {moment(surveyFieldData.updatedAt).format('MMMM Do YYYY, h:mm:ss a')}
                </span>
              </Typography>
            )}

            {surveyFieldData.submittedAt && (
              <Typography component="div" variant="body2" sx={{ lineHeight: 1.2 }}>
                <span style={{ fontWeight: 700 }}>Submitted At-</span>{' '}
                <span style={{ fontWeight: 400 }}>
                  {moment(surveyFieldData.submittedAt).format('MMMM Do YYYY, h:mm:ss a')}
                </span>
              </Typography>
            )}
          </Box>

          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, mt: 1 }}>
            Select Year
          </Typography>

          {surveyFieldData?.sections?.map((value) => {
            const year = Number(value?.slug.match(/\d{4}$/)?.[0]);
            const sections = value?.sections ?? [];

            const completedStages = sections.filter((section) => {
              const questions = section.questions ?? [];

              return (
                questions.length > 0 &&
                questions.every(
                  (question) =>
                    question.answer?.value !== undefined &&
                    question.answer?.value !== null &&
                    String(question.answer.value).trim() !== ''
                )
              );
            }).length;

            return (
              <Card
                key={year}
                sx={{
                  mb: 2,
                  borderRadius: 3,
                  boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
                  cursor: 'pointer',
                  transition: '0.2s',
                  '&:hover': { transform: 'scale(1.01)' }
                }}
                onClick={() => setActiveYear(year)}
              >
                <CardContent
                  sx={{
                    p: '20px !important',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {value.name}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      {completedStages}/{value.sections?.length} stages completed
                    </Typography>

                    <LinearProgress
                      variant="determinate"
                      value={(completedStages / value.sections?.length) * 100}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: '#f0f0f0',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: value.isRed ? '#ff4d4f' : '#00e676'
                        }
                      }}
                    />
                  </Box>

                  <ArrowForwardIcon color="action" sx={{ ml: 2 }} />
                </CardContent>
              </Card>
            );
          })}
        </Box>
      </Box>
    );
  }

  const activeYearSection = surveyFieldData?.sections?.find((section) => {
    const year = Number(section?.slug?.match(/\d{4}$/)?.[0]);
    return year === activeYear;
  });

  // Render Survey details (Questions popping up) when a year is clicked
  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f9f9f9',
        pb: 4,
        animation: 'fadeIn 0.3s ease-in-out',
        '@keyframes fadeIn': {
          from: { opacity: 0, transform: 'translateY(10px)' },
          to: { opacity: 1, transform: 'translateY(0)' }
        }
      }}
    >
      <Box
        sx={{
          bgcolor: 'white',
          px: 2,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid #eaeaea',
          mb: 3
        }}
      >
        <IconButton sx={{ mr: 1 }} onClick={() => setActiveYear(null)}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
            Crop year {activeYear}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {surveyFieldData.name}
          </Typography>
        </Box>
        <Chip
          label={surveyFieldData.status}
          sx={{
            bgcolor: statusColor.bg,
            color: statusColor.text,
            fontWeight: 'bold',
            borderRadius: 1
          }}
        />
      </Box>

      <Card
        sx={{
          width: '93%',
          margin: '0 auto',
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
          borderRadius: 2
        }}
      >
        <Box sx={{ p: 4, bgcolor: 'white' }}>
          {activeYearSection ? (
            <Accordion
              key={activeYearSection.sectionId}
              defaultExpanded
              disableGutters
              sx={{
                mb: 2,
                boxShadow: 'none',
                border: 'none',
                '&:before': { display: 'none' }
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  p: 0,
                  minHeight: '48px !important',
                  '& .MuiAccordionSummary-content': {
                    my: '0 !important'
                  }
                }}
              >
                <Typography variant="h6" fontWeight="bold">
                  {activeYearSection.name}
                </Typography>
              </AccordionSummary>

              <AccordionDetails sx={{ p: 0, pt: 1, pb: 2 }}>
                {activeYearSection.sections?.map((subSection) => (
                  <Accordion
                    key={subSection.sectionId}
                    defaultExpanded
                    disableGutters
                    sx={{
                      boxShadow: 'none',
                      border: 'none',
                      '&:before': { display: 'none' },
                      ml: 2
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        p: 0,
                        minHeight: '40px !important',
                        '& .MuiAccordionSummary-content': {
                          my: '0 !important'
                        }
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight="bold" color="text.secondary">
                        {subSection.name}
                      </Typography>
                    </AccordionSummary>

                    <AccordionDetails
                      sx={{
                        p: 0,
                        pt: 1,
                        pb: 3,
                        pl: 3
                      }}
                    >
                      {subSection.questions?.map((q) => (
                        <ReadOnlyQuestionRow key={q.formQuestionId} question={q} />
                      ))}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </AccordionDetails>
            </Accordion>
          ) : (
            <Typography color="text.disabled">No data available for {activeYear}.</Typography>
          )}
        </Box>
      </Card>
    </Box>
  );
};
